import React, { useEffect } from 'react';
import { Calendar, Share2, CheckCircle, X, Camera, Edit, Trash2, Users, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isEnabled, TOURNAMENT_ENTITIES } from '../access';
import Breadcrumb from '../../components/common/Breadcrumb';
import { btn } from '../../utils/buttonStyles';

/**
 * TournamentHeader - Self-contained tournament header component
 *
 * Displays tournament banner, title, dates, info cards, and registration status
 *
 * @param {Object} props
 * @param {Object} props.tournament - Tournament data
 * @param {number} props.participantCount - Number of participants (from summary)
 * @param {string} props.tournamentImageUrl - URL for tournament image
 * @param {Object} props.registrationStatus - { type: 'success'|'error', message: string }
 * @param {Function} props.onShare - Callback when share button is clicked
 * @param {boolean} props.isEditing - Whether in edit mode (shows camera icon)
 * @param {Function} props.onImageUploadClick - Callback when camera icon is clicked
 * @param {boolean} props.isOrganizer - Whether current user is organizer
 * @param {Function} props.onEdit - Edit button callback
 * @param {Function} props.onDelete - Delete button callback
 */
export default function TournamentHeader({
  tournament,
  tournamentVO,
  participantCount,
  tournamentImageUrl,
  registrationStatus,
  onShare,
  isEditing = false,
  onImageUploadClick,
  isOrganizer = false,
  onEdit,
  onDelete,
  fromSquad = null,
  fromAcademy = null,
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
  }, [participantCount, tournament?.participants]);

  // Use access control if VO is available, otherwise fall back to old logic
  const canEdit = tournamentVO
    ? isEnabled(user?.id, TOURNAMENT_ENTITIES.TOURNAMENT_EDIT, tournamentVO)
    : (isOrganizer && tournament?.status !== 'completed');

  const canDelete = tournamentVO
    ? isEnabled(user?.id, TOURNAMENT_ENTITIES.TOURNAMENT_DELETE, tournamentVO)
    : (isOrganizer && tournament?.status !== 'completed');

  if (!tournament) {
    return null;
  }

  return (
    <>
      {/* Breadcrumb */}
      <div className="mb-4">
        <Breadcrumb
          items={fromSquad ? [
            { label: 'Home', path: '/' },
            { label: fromSquad.name, path: `/squads/${fromSquad.id}` },
            { label: 'Tournaments', path: `/squads/${fromSquad.id}/tournaments` },
            { label: tournament.tournamentName || 'Loading...' }
          ] : fromAcademy ? [
            { label: 'Home', path: '/' },
            { label: fromAcademy.name, path: `/academy/${fromAcademy.id}` },
            { label: 'Tournaments', path: `/academy/${fromAcademy.id}?tab=tournaments` },
            { label: tournament.tournamentName || 'Loading...' }
          ] : [
            { label: 'Home', path: '/' },
            { label: 'Tournaments', path: '/tournaments' },
            { label: tournament.tournamentName || 'Loading...' }
          ]}
        />
      </div>

      {/* Banner Image */}
      {tournamentImageUrl ? (
        <div className="mb-6 rounded-2xl overflow-hidden relative" style={{ aspectRatio: '16 / 9' }}>
          <img
            src={tournamentImageUrl}
            alt={tournament.tournamentName}
            className="w-full h-full object-cover"
          />
          {isEditing && onImageUploadClick && (
            <button
              onClick={onImageUploadClick}
              className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-colors cursor-pointer"
              title="Change tournament image"
            >
              <Camera className="w-6 h-6" />
            </button>
          )}
        </div>
      ) : isEditing && onImageUploadClick ? (
        <button
          onClick={onImageUploadClick}
          className="mb-6 w-full rounded-2xl border-2 border-dashed border-gray-500 hover:border-blue-500 hover:bg-blue-500/5 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-blue-400 transition-colors cursor-pointer"
          style={{ aspectRatio: '16 / 9' }}
          title="Upload banner image"
        >
          <Camera className="w-10 h-10" />
          <span className="text-sm font-medium">Upload Banner Image</span>
        </button>
      ) : null}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-x-3">
            {tournamentImageUrl ? (
              <img
                src={tournamentImageUrl}
                alt=""
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover"
              />
            ) : (
              <span className="text-3xl sm:text-4xl">{tournament.emoji || '🏆'}</span>
            )}
            <div>
              <h1 className={`text-2xl sm:text-3xl font-bold ${theme.text}`}>
                {tournament.tournamentName}
              </h1>
              {tournament.ownerType === 'group' && tournament.ownerName && (
                <button
                  onClick={() => navigate(`/squads/${tournament.ownerId}`)}
                  className={`mt-1 flex items-center gap-x-1 text-sm ${theme.link} ${theme.linkHover} transition-colors`}
                >
                  <Users className="w-4 h-4" />
                  <span>{tournament.ownerName}</span>
                </button>
              )}
              {tournament.ownerType === 'academy' && tournament.ownerName && (
                <button
                  onClick={() => navigate(`/academy/${fromAcademy?.id || tournament.ownerId}`)}
                  className={`mt-1 flex items-center gap-x-1 text-sm ${theme.link} ${theme.linkHover} transition-colors`}
                >
                  <Users className="w-4 h-4" />
                  <span>{tournament.ownerName}</span>
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={btn.group}>
            {canEdit && !isEditing && onEdit && (
              <button
                onClick={onEdit}
                className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-x-1`}
                title="Edit Tournament"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>
            )}
            {canDelete && !isEditing && onDelete && (
              <button
                onClick={onDelete}
                className={`${btn.base} ${btn.sm} ${theme.buttonDanger} flex items-center gap-x-1`}
                title="Delete Tournament"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex items-center gap-x-1`}
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            {onShare && (
              <button
                onClick={() => onShare(tournament)}
                className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex items-center gap-x-1`}
                title="Share"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
          </div>
        </div>

        {/* Tournament Dates */}
        <div className="px-2">
          <div className={`${theme.textSecondary} text-sm sm:text-base`}>
            <div className="flex items-start">
              <Calendar className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
              <span>
                {tournament.startDate
                  ? new Date(tournament.startDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })
                  : 'TBD'}
                {' - '}
                {tournament.endDate
                  ? new Date(tournament.endDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })
                  : 'TBD'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tournament Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8">
        <div className={`${theme.card} rounded-lg p-3 sm:p-4 text-center`}>
          <div className={`text-xl sm:text-2xl font-bold ${theme.text}`}>Participants</div>
          <div className={`text-xs sm:text-sm ${theme.textMuted}`}>
            {participantCount ?? tournament?.participants?.length ?? 0}
          </div>
        </div>

        <div className={`${theme.card} rounded-lg p-3 sm:p-4 text-center`}>
          <div className={`text-sm sm:text-lg font-bold ${theme.text} leading-tight`}>Status</div>
          <div className={`text-xs sm:text-sm ${theme.textMuted}`}>
            {tournament.status?.replace(/_/g, ' ') || 'Draft'}
          </div>
        </div>

        <div className={`${theme.card} rounded-lg p-3 sm:p-4 text-center`}>
          <div className={`text-sm sm:text-lg font-bold ${theme.text} leading-tight`}>Difficulty</div>
          <div className={`text-xs sm:text-sm ${theme.textMuted}`}>
            {tournament.difficultyLevel?.replace(/_/g, ' ') || 'All Levels'}
          </div>
        </div>
      </div>

      {/* Prize Details */}
      {tournament.prizeDetail && (
        <div className="mb-8">
          <div className={`${theme.card} rounded-lg p-3 sm:p-4`}>
            <div className={`text-xs sm:text-sm ${theme.textMuted} mb-1`}>Prize Details</div>
            <div className={`text-sm font-medium ${theme.text}`}>
              {typeof tournament.prizeDetail === 'object'
                ? tournament.prizeDetail.description || JSON.stringify(tournament.prizeDetail)
                : tournament.prizeDetail}
            </div>
          </div>
        </div>
      )}

      {/* Registration Status Message */}
      {registrationStatus?.message && (
        <div className={`mb-6 p-4 rounded-lg text-center ${
          registrationStatus.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center justify-center gap-x-2">
            {registrationStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <X className="w-5 h-5" />
            )}
            <span>{registrationStatus.message}</span>
          </div>
        </div>
      )}
    </>
  );
}
